import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { PhoneInput } from "@/components/ui/phone-input";
import { useToast } from "@/hooks/use-toast";
import { FileUp, Send, Shield, Bell, CheckCircle, AlertCircle, Phone } from "lucide-react";
import { isValidPhoneNumber } from "libphonenumber-js";

interface JobApplicationProps {
  jobId: string;
  jobTitle: string;
  onApplicationSubmitted?: () => void;
}

export const JobApplication = ({ jobId, jobTitle, onApplicationSubmitted }: JobApplicationProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [coverLetter, setCoverLetter] = useState("");
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  
  // Profile data
  const [profile, setProfile] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // CV selection
  const [cvOption, setCvOption] = useState<"profile" | "upload">("profile");
  
  // GDPR and notification preferences
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [smsNotifications, setSmsNotifications] = useState(false);
  const [whatsappNotifications, setWhatsappNotifications] = useState(false);
  
  // Phone verification
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (user && open) {
      fetchProfile();
    }
  }, [user, open]);

  const fetchProfile = async () => {
    try {
      setProfileLoading(true);
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error) throw error;
      
      setProfile(profileData);
      if (profileData?.phone_number) {
        setPhoneNumber(profileData.phone_number);
      }
      
      // Set default CV option based on whether user has profile CV
      if (profileData?.cv_url || profileData?.resume_url) {
        setCvOption("profile");
      } else {
        setCvOption("upload");
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number",
        variant: "destructive"
      });
      return;
    }

    setIsVerifying(true);
    try {
      const { error } = await supabase.functions.invoke('send-sms-notification', {
        body: {
          to: phoneNumber,
          message: `Your verification code is: ${Math.floor(100000 + Math.random() * 900000)}`,
          verification: true
        }
      });

      if (error) throw error;

      toast({
        title: "Verification Code Sent",
        description: "Please check your phone for the verification code",
      });
      setShowVerification(true);
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast({
        title: "Failed to Send Code",
        description: "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const verifyPhoneNumber = async () => {
    if (!verificationCode.trim()) {
      toast({
        title: "Verification Code Required",
        description: "Please enter the verification code",
        variant: "destructive"
      });
      return;
    }

    setIsVerifying(true);
    try {
      // Update profile with verified phone number
      const { error } = await supabase
        .from('profiles')
        .update({
          phone_number: phoneNumber,
          phone_verified: true,
          sms_alerts_enabled: true
        })
        .eq('user_id', user?.id);

      if (error) throw error;

      // Update local profile state
      setProfile(prev => ({
        ...prev,
        phone_number: phoneNumber,
        phone_verified: true,
        sms_alerts_enabled: true
      }));

      setShowVerification(false);
      setVerificationCode("");

      toast({
        title: "Phone Verified!",
        description: "Your phone number has been verified successfully",
      });
    } catch (error) {
      console.error('Error verifying phone number:', error);
      toast({
        title: "Verification Failed",
        description: "Failed to verify phone number. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF or Word document",
          variant: "destructive"
        });
        return;
      }

      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB",
          variant: "destructive"
        });
        return;
      }

      setCvFile(file);
    }
  };

  const uploadCV = async (): Promise<string | null> => {
    if (!cvFile || !user) {
      console.error('No CV file or user for upload');
      return null;
    }

    console.log('Starting CV upload for file:', cvFile.name);
    const fileName = `${user.id}/${Date.now()}-${cvFile.name}`;
    
    try {
      const { error } = await supabase.storage
        .from("cvs")
        .upload(fileName, cvFile, {
          upsert: true
        });

      if (error) {
        console.error("CV upload error:", error);
        throw new Error(`Failed to upload CV: ${error.message}`);
      }

      console.log('CV uploaded successfully:', fileName);
      return fileName;
    } catch (error) {
      console.error('Error during CV upload:', error);
      throw error;
    }
  };

  const submitApplication = async () => {
    console.log('Starting job application submission...');
    
    if (!user) {
      console.error('No user found - authentication required');
      toast({
        title: "Authentication required",
        description: "Please sign in to apply for jobs",
        variant: "destructive"
      });
      return;
    }

    // Validate job exists
    const { data: jobExists } = await supabase
      .from("jobs")
      .select("id, title, posted_by")
      .eq("id", jobId)
      .eq("status", "active")
      .maybeSingle();

    if (!jobExists) {
      console.error('Job not found or inactive:', jobId);
      toast({
        title: "Job unavailable",
        description: "This job is no longer available for applications",
        variant: "destructive"
      });
      return;
    }

    if (!gdprAccepted) {
      console.log('GDPR consent not accepted');
      toast({
        title: "GDPR consent required",
        description: "Please accept the terms and privacy policy to continue",
        variant: "destructive"
      });
      return;
    }

    if ((smsNotifications || whatsappNotifications) && !profile?.phone_verified) {
      console.log('Phone verification required for SMS/WhatsApp notifications');
      toast({
        title: "Phone verification required",
        description: "Please verify your phone number for SMS/WhatsApp notifications",
        variant: "destructive"
      });
      return;
    }

    // Validate cover letter length
    if (coverLetter.length > 1000) {
      toast({
        title: "Cover letter too long",
        description: "Please keep your cover letter under 1000 characters",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    console.log('Validations passed, proceeding with application...');
    
    try {
      // Check if already applied
      console.log('Checking for existing application...');
      const { data: existingApplication } = await supabase
        .from("applications")
        .select("id")
        .eq("job_id", jobId)
        .eq("applicant_id", user.id)
        .maybeSingle();

      if (existingApplication) {
        console.log('User has already applied for this job');
        toast({
          title: "Already applied",
          description: "You have already applied for this job",
          variant: "destructive"
        });
        return;
      }

      // Determine CV URL to use
      console.log('Processing CV selection...');
      let cvUrl = null;
      if (cvOption === "profile") {
        cvUrl = profile?.cv_url || profile?.resume_url;
        console.log('Using profile CV:', cvUrl ? 'found' : 'not found');
      } else if (cvFile) {
        console.log('Uploading new CV file...');
        try {
          cvUrl = await uploadCV();
          console.log('CV uploaded successfully:', cvUrl);
        } catch (uploadError) {
          console.error('CV upload failed:', uploadError);
          toast({
            title: "CV upload failed",
            description: "Failed to upload your CV. Please try again.",
            variant: "destructive"
          });
          return;
        }
      }

      // Submit application with notification preferences
      console.log('Submitting application to database...');
      const { data: application, error } = await supabase
        .from("applications")
        .insert({
          job_id: jobId,
          applicant_id: user.id,
          cover_letter: coverLetter || null,
          cv_url: cvUrl,
          status: "pending",
          notification_preferences: {
            email: true, // Always enabled
            sms: smsNotifications,
            whatsapp: whatsappNotifications,
            phone: profile?.phone_number || phoneNumber
          }
        })
        .select()
        .single();

      if (error) {
        console.error('Application submission failed:', error);
        throw error;
      }

      console.log('Application submitted successfully:', application.id);

      // Send SMS notification to employer (fire-and-forget)
      console.log('Attempting to send SMS notification to employer...');
      if (jobExists) {
        // Get employer profile
        const { data: employerProfile } = await supabase
          .from("profiles")
          .select("full_name, phone_number")
          .eq("user_id", jobExists.posted_by)
          .single();

        // Fire-and-forget SMS notification - don't await or block on failure
        supabase.functions.invoke('send-sms-notification', {
          body: {
            to: employerProfile?.phone_number,
            message: `New job application received for "${jobExists.title}" from ${user.email}. Check your dashboard for details.`,
            employerId: jobExists.posted_by,
            applicationId: application.id
          }
        }).then(() => {
          console.log('SMS notification sent successfully');
        }).catch((smsError) => {
          console.log('SMS notification failed (non-blocking):', smsError);
          // This is intentionally non-blocking - SMS failure should not affect application
        });
      }

      console.log('Application process completed successfully');
      toast({
        title: "Application submitted!",
        description: "Your application has been sent successfully",
      });

      // Reset form
      setCoverLetter("");
      setCvFile(null);
      setGdprAccepted(false);
      setSmsNotifications(false);
      setWhatsappNotifications(false);
      setVerificationCode("");
      setOpen(false);
      onApplicationSubmitted?.();

    } catch (error: any) {
      console.error("Application submission error:", error);
      
      // More specific error messages
      let errorMessage = "Failed to submit application. Please try again.";
      if (error?.message?.includes('auth')) {
        errorMessage = "Authentication error. Please sign in again.";
      } else if (error?.message?.includes('storage')) {
        errorMessage = "File upload failed. Please try uploading your CV again.";
      } else if (error?.message?.includes('unique')) {
        errorMessage = "You have already applied for this job.";
      }
      
      toast({
        title: "Application Failed",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Button onClick={() => navigate('/auth')}>
        <Send className="h-4 w-4 mr-1" />
        Apply Now
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Send className="h-4 w-4 mr-1" />
          Apply Now
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Apply for {jobTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Cover Letter Section */}
          <div>
            <Label htmlFor="cover-letter" className="text-base font-medium">Cover Letter</Label>
            <Textarea
              id="cover-letter"
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              placeholder="Tell us why you're the perfect fit for this role. Highlight your relevant experience, skills, and what excites you about this opportunity..."
              rows={6}
              className="mt-2 min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {coverLetter.length}/1000 characters
            </p>
          </div>

          {/* CV Section */}
          <div>
            <Label className="text-base font-medium">CV/Resume</Label>
            {profileLoading ? (
              <div className="mt-2 p-4 bg-muted/50 rounded-md">
                <div className="animate-pulse">Loading profile...</div>
              </div>
            ) : (
              <div className="mt-2 space-y-3">
                <RadioGroup value={cvOption} onValueChange={(value) => setCvOption(value as "profile" | "upload")}>
                  {(profile?.cv_url || profile?.resume_url) && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="profile" id="profile-cv" />
                      <Label htmlFor="profile-cv" className="flex items-center space-x-2 flex-1">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Use my profile CV/Resume</span>
                      </Label>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="upload" id="upload-cv" />
                    <Label htmlFor="upload-cv" className="flex items-center space-x-2">
                      <FileUp className="h-4 w-4" />
                      <span>Upload a different CV/Resume</span>
                    </Label>
                  </div>
                </RadioGroup>

                {!(profile?.cv_url || profile?.resume_url) && (
                  <div className="text-sm text-muted-foreground bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    No CV found in your profile. You can upload one now or add it to your profile later.
                  </div>
                )}

                {cvOption === "upload" && (
                  <div className="space-y-2">
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="flex-1"
                    />
                    {cvFile && (
                      <div className="flex items-center text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                        <FileUp className="h-4 w-4 mr-2" />
                        {cvFile.name}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Accepted formats: PDF, DOC, DOCX (max 5MB)
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification Preferences */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Bell className="h-4 w-4" />
              <Label className="text-base font-medium">Notification Preferences</Label>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Choose how you'd like to be notified about your application status
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-2 bg-muted/50 p-3 rounded-md">
                <div className="w-4 h-4 bg-primary rounded-sm flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <Label className="text-sm font-medium">
                  Email notifications (always enabled)
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sms-notifications"
                  checked={smsNotifications}
                  onCheckedChange={(checked) => setSmsNotifications(checked === true)}
                />
                <Label htmlFor="sms-notifications" className="text-sm">
                  SMS notifications
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="whatsapp-notifications"
                  checked={whatsappNotifications}
                  onCheckedChange={(checked) => setWhatsappNotifications(checked === true)}
                />
                <Label htmlFor="whatsapp-notifications" className="text-sm">
                  WhatsApp notifications
                </Label>
              </div>
              
              {(smsNotifications || whatsappNotifications) && (
                <div className="mt-3 space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="h-4 w-4" />
                    <span className="font-medium">Phone Number for Notifications</span>
                  </div>
                  
                  {profile?.phone_verified ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-800">
                          Verified: {profile.phone_number}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-4 w-4 text-yellow-600" />
                          <span className="text-sm text-yellow-800">
                            Phone verification required for SMS/WhatsApp notifications
                          </span>
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="phone-input" className="text-sm font-medium">Phone Number</Label>
                        <PhoneInput
                          id="phone-input"
                          value={phoneNumber}
                          onChange={(value) => setPhoneNumber(value || "")}
                          className="mt-1"
                          placeholder="Enter phone number"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Include country code (e.g., +65 for Singapore, +94 for Sri Lanka)
                        </p>
                      </div>
                      
                      <div className="flex space-x-2">
                        {!profile?.phone_verified && (
                          <Dialog open={showVerification} onOpenChange={setShowVerification}>
                            <DialogTrigger asChild>
                              <Button
                                onClick={sendVerificationCode}
                                disabled={!phoneNumber || !isValidPhoneNumber(phoneNumber) || isVerifying}
                                variant="outline"
                                size="sm"
                              >
                                {isVerifying ? 'Sending...' : 'Verify Phone'}
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Verify Phone Number</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                  Enter the verification code sent to {phoneNumber}
                                </p>
                                <div>
                                  <Label htmlFor="verification-code">Verification Code</Label>
                                  <Input
                                    id="verification-code"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    placeholder="123456"
                                    className="mt-1"
                                  />
                                </div>
                                <div className="flex space-x-2">
                                  <Button
                                    onClick={verifyPhoneNumber}
                                    disabled={!verificationCode || isVerifying}
                                    className="flex-1"
                                  >
                                    {isVerifying ? 'Verifying...' : 'Verify'}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => setShowVerification(false)}
                                    disabled={isVerifying}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* GDPR Compliance Section */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4" />
              <Label className="text-base font-medium">Privacy & Terms</Label>
            </div>
            <div className="flex items-start space-x-2">
              <Checkbox
                id="gdpr-consent"
                checked={gdprAccepted}
                onCheckedChange={(checked) => setGdprAccepted(checked === true)}
                className="mt-1"
              />
              <div className="flex-1">
                <Label htmlFor="gdpr-consent" className="text-sm leading-relaxed">
                  I consent to the processing of my personal data for recruitment purposes and agree to the{" "}
                  <a href="/privacy-policy" target="_blank" className="text-primary hover:underline">Privacy Policy</a> and{" "}
                  <a href="/terms-of-service" target="_blank" className="text-primary hover:underline">Terms of Service</a>.
                  I understand that my data will be shared with the employer for this specific job application. *
                </Label>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Your data is processed in accordance with GDPR regulations. You can withdraw consent at any time.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <Button
              onClick={submitApplication}
              disabled={loading || !gdprAccepted}
              className="flex-1"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting Application...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              size="lg"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};